import Icon from './Icon';

/** Page numbers to show, with '…' gaps: 1 … 4 5 6 … 30 */
export function getPageItems(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, '…', pageCount];
  if (page >= pageCount - 3) return [1, '…', ...Array.from({ length: 5 }, (_, i) => pageCount - 4 + i)];
  return [1, '…', page - 1, page, page + 1, '…', pageCount];
}

const fmt = (n) => n.toLocaleString('en-US');

/**
 * Table footer: "Showing 21–40 of 90 {noun}", page controls and an optional
 * rows-per-page selector (pass `pageSizeOptions` + `onPageSizeChange`). `summary` replaces the
 * "Showing …" text. Pages are 1-based.
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  noun = 'results',
  pageSizeOptions,
  onPageSizeChange,
  disabled = false,
  summary,
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const go = (p) => !disabled && p >= 1 && p <= pageCount && p !== page && onPageChange(p);
  const sizes = pageSizeOptions && [...new Set([...pageSizeOptions, pageSize])].sort((a, b) => a - b);

  const navBtn =
    'h-8 px-2.5 rounded-lg inline-flex items-center gap-1 text-xs font-medium text-on-surface-variant bg-surface-container-lowest shadow-xs hover:bg-surface-container hover:text-on-surface disabled:opacity-40 disabled:hover:bg-surface-container-lowest disabled:cursor-not-allowed outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot';

  return (
    <div className="px-5 py-3.5 bg-surface-container-low/40 border-t border-surface-container flex flex-col md:flex-row items-center justify-between gap-3">
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-on-surface-variant">
        {summary ?? (
        <span>
          Showing{' '}
          <strong className="font-data-mono text-on-surface">
            {fmt(from)}–{fmt(to)}
          </strong>{' '}
          of <strong className="font-data-mono text-on-surface">{fmt(total)}</strong> {noun}
        </span>
        )}
        {sizes && onPageSizeChange && (
          <label className="inline-flex items-center gap-2">
            Rows per page
            <select
              value={pageSize}
              disabled={disabled}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 pl-2 pr-7 rounded-lg border border-outline-variant/50 bg-surface-container-lowest font-data-mono text-xs text-on-surface outline-none focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)] disabled:opacity-50"
            >
              {sizes.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <nav className="flex items-center gap-1" aria-label="Pagination">
        <button type="button" className={navBtn} disabled={disabled || page === 1} onClick={() => go(page - 1)}>
          <Icon name="chevron_left" size={18} />
          <span className="hidden sm:inline">Previous</span>
        </button>
        {getPageItems(page, pageCount).map((item, i) =>
          item === '…' ? (
            <span key={`gap-${i}`} className="w-8 text-center text-xs text-on-surface-variant" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-label={`Page ${item}`}
              aria-current={item === page ? 'page' : undefined}
              disabled={disabled}
              onClick={() => go(item)}
              className={`min-w-8 h-8 px-2 rounded-lg text-xs font-data-mono outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot disabled:cursor-not-allowed ${
                item === page
                  ? 'bg-primary-container text-on-primary font-bold shadow-xs'
                  : 'bg-surface-container-lowest text-on-surface shadow-xs hover:bg-surface-container'
              }`}
            >
              {fmt(item)}
            </button>
          ),
        )}
        <button type="button" className={navBtn} disabled={disabled || page >= pageCount} onClick={() => go(page + 1)}>
          <span className="hidden sm:inline">Next</span>
          <Icon name="chevron_right" size={18} />
        </button>
      </nav>
    </div>
  );
}
